import { createFileRoute, Outlet, useNavigate, useLocation } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import { ConfirmDialog, GenericDetailPage, EventInfoBox, Tabs, Spinner } from '@repo/components';
import { EventForm } from '@repo/forms';
import { EventEmitter, getVerifyPermission } from '@repo/shared/helpers';
import { guardedClient as request } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';
import { useLocalStorage } from '@repo/components/hooks';
import { PERMISSIONS, Event, Task } from '@repo/shared/domain';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getEvent, updateEvent, deleteEvent } from 'src/api/personal/events';
import { getEventTypes } from 'src/api/admin/types';
import { getAllTags } from 'src/api/admin/tags';
import { getApprovalsForEvent } from 'src/api/personal/eventDetail';
import DuplicateEventDialog from 'src/components/DuplicateEventDialog';

export const Route = createFileRoute('/personal_/events/$eventId')({
	component: EventDetailLayout,
});

function EventDetailLayout() {
	const { eventId } = Route.useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const { value: locallyStoredBackUrl } = useLocalStorage<string>('personalDashboardEventsBackUrl');
	const [isFormOpened, setIsFormOpened] = useState(false);
	const [isEventDuplicateDialogOpen, setIsEventDuplicateDialogOpen] = useState(false);
	const [isEventArchiveDialogOpen, setIsEventArchiveDialogOpen] = useState(false);

	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);
	const hasOwnEventsView = verifyPermission([PERMISSIONS.personalOwnEventsView]);

	const {
		data: event,
		isLoading: eventLoading,
		refetch: refetchEvent,
	} = useQuery({
		queryKey: ['personal-event', eventId, !hasAllEventsView],
		queryFn: () => getEvent(eventId, !hasAllEventsView),
		enabled: !authLoading && !!authenticatedUser && (hasAllEventsView || hasOwnEventsView),
	});

	const { config, loading: configLoading } = useConfig();
	const featureFlags = config?.featureFlags || {};
	const usesSubEvents = featureFlags?.USES_SUBCONTRACTING || false;
	const usesTags = featureFlags?.USES_TAGS || false;

	const { data: typesData } = useQuery({
		queryKey: ['personal-event-types'],
		queryFn: () => getEventTypes(),
	});

	const { data: tagsData } = useQuery({
		queryKey: ['tags'],
		queryFn: () => getAllTags(),
		enabled: usesTags,
	});

	const { data: approvalsData } = useQuery({
		queryKey: ['personal-event-approvals', eventId],
		queryFn: () => getApprovalsForEvent(eventId),
		enabled: !!event,
	});
	const isOwnEvent = useMemo(
		() => !!event?.adminIds?.includes(authenticatedUser?.id || ''),
		[event, authenticatedUser?.id],
	);
	const types = typesData?.data || [];
	const tags = tagsData || [];
	const approvals = approvalsData?.data || [];
	const hasApprovalsTodo = approvals.find((t: Task) => t.status === 'todo');

	useBreadcrumbLevel(0, 'Personal Dashboard', '/personal');
	useBreadcrumbLevel(1, 'Events', '/personal/events');
	useBreadcrumbLevel(2, event?.name ?? 'Event');

	useEffect(() => {
		if (!authLoading && !hasAllEventsView && !hasOwnEventsView) {
			navigate({ to: '/personal/overview' });
		}
	}, [authLoading, hasAllEventsView, hasOwnEventsView, navigate]);

	const { tabs, tabIndexPerTabId } = useMemo(() => {
		if (!event)
			return {
				tabs: [] as { id: string; label: string; error?: boolean }[],
				tabIndexPerTabId: {} as Record<string, number>,
			};
		const builtTabs = [
			...(event?.type?.Form &&
			event.type.form !== 'event' &&
			isOwnEvent &&
			verifyPermission([PERMISSIONS.personalOwnEventsDetails])
				? [{ id: 'details', label: 'Details' }]
				: []),
			...(verifyPermission([PERMISSIONS.personalAllEventsUsersView]) ||
			(verifyPermission([PERMISSIONS.personalOwnEventsUsersView]) && isOwnEvent)
				? [{ id: 'users', label: 'Users' }]
				: []),
			...(event?.projectId && usesSubEvents ? [{ id: 'subcontracting', label: 'Subcontracting' }] : []),
			{ id: 'tasks', label: 'Tasks' },
			{ id: 'approvals', label: 'Approvals', error: !!hasApprovalsTodo },
			{ id: 'documents', label: 'Documents' },
			{ id: 'logs', label: 'Logs' },
		];
		const tabIndexPerTabId = builtTabs.reduce(
			(acc, cur, index) => ({ ...acc, [cur.id]: index }),
			{} as Record<string, number>,
		);
		return { tabs: builtTabs, tabIndexPerTabId };
	}, [event, isOwnEvent, usesSubEvents, verifyPermission, hasApprovalsTodo]);

	const pathSegments = location.pathname.split('/').filter(Boolean);
	const currentSegment = pathSegments[pathSegments.length - 1];
	const segmentBefore = pathSegments[pathSegments.length - 2];
	const nestedTabIds = ['approvals', 'documents', 'tasks'];
	const isNestedTab = nestedTabIds.includes(segmentBefore) && currentSegment !== segmentBefore;
	const effectiveTabId = isNestedTab ? segmentBefore : currentSegment;
	const selectedTabIndex = tabIndexPerTabId[effectiveTabId] ?? (tabs.length ? 0 : -1);

	// Redirect to first tab when on event root (e.g. /personal/events/123)
	useEffect(() => {
		if (!event || tabs.length === 0) return;
		if (currentSegment === eventId) {
			const firstTab = tabs[0];
			if (firstTab) {
				navigate({ to: `/personal/events/${eventId}/${firstTab.id}` });
			}
		}
	}, [event, tabs, currentSegment, eventId, navigate]);

	const goBack = useCallback(() => {
		if (locallyStoredBackUrl) {
			window.location.href = locallyStoredBackUrl;
			return;
		}
		navigate({ to: '/personal/events' });
	}, [locallyStoredBackUrl, navigate]);

	const defaultEventData = useMemo(() => ({ ...event }), [event]);

	const handleEditEvent = useCallback(
		async (formData: Record<string, unknown> | undefined) => {
			if (!event || !formData) return;
			const dataToSend = { ...formData };
			delete (dataToSend as Record<string, unknown>).type;
			try {
				await updateEvent(event.id, isOwnEvent, dataToSend);
				EventEmitter.emit('toast', { type: 'success', message: 'Event saved' });
				queryClient.invalidateQueries({ queryKey: ['personal-event', eventId] });
				refetchEvent();
				setIsFormOpened(false);
			} catch {
				EventEmitter.emit('toast', {
					type: 'error',
					message: 'Failed to save Event. Did you enter all data correctly?',
				});
			}
		},
		[event, isOwnEvent, eventId, queryClient, refetchEvent],
	);

	const handleTabChange = useCallback(
		(newValue: number) => {
			const tab = tabs[newValue];
			if (tab) {
				navigate({ to: `/personal/events/${eventId}/${tab.id}` });
			}
		},
		[tabs, eventId, navigate],
	);

	const archiveEvent = useCallback(async () => {
		if (!event) return;
		try {
			await deleteEvent(event.id, isOwnEvent);
			queryClient.invalidateQueries({ queryKey: ['personal-events'] });
			setIsEventArchiveDialogOpen(false);
			goBack();
		} catch {
			EventEmitter.emit('toast', { type: 'error', message: 'Failed to archive event.' });
		}
	}, [event, isOwnEvent, queryClient, goBack]);

	const duplicateEvent = useCallback(
		async (formData: Record<string, unknown> | undefined) => {
			if (!formData) return;
			try {
				const { body } = await request.post<undefined, { id: string }>('/api/personal-dashboard/events', formData, {
					schema: undefined,
				});
				queryClient.invalidateQueries({ queryKey: ['personal-events'] });
				EventEmitter.emit('toast', { type: 'success', message: 'Event duplicated' });
				setIsEventDuplicateDialogOpen(false);
				navigate({ to: `/personal/events/${body.id}` });
			} catch {
				EventEmitter.emit('toast', { type: 'error', message: 'Failed to duplicate event.' });
			}
		},
		[queryClient, navigate],
	);

	if (authLoading || eventLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (!event) {
		if (!hasAllEventsView) {
			navigate({
				to: '/403',
				search: { permissions: 'personalAllEventsView', occurredAt: location.pathname, method: 'GET' },
			});
		} else {
			navigate({ to: '/personal/events' });
		}
		return null;
	}

	return (
		<>
			<div className="flex justify-center">
				<div className="max-w-[1280px] w-full">
					<GenericDetailPage>
						<GenericDetailPage.Content className="!px-0">
							<GenericDetailPage.Content.Notifications>
								{event.markedForReview?.length && event.markedForReview.includes('invalidMain') ? (
									<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
										Invalid main company
									</Alert>
								) : null}
								{event.status === 'inReview' ? (
									<Alert severity="error" variant="filled" sx={{ '& ul': { margin: 0, paddingLeft: 2 } }}>
										Event needs to be reviewed before allowing access
										<ul>{event?.requirements?.map((r) => r.issues?.map((i, idx) => <li key={idx}>{i}</li>))}</ul>
									</Alert>
								) : null}
								{event.status === 'exception' ? (
									<Alert severity="error" variant="filled">
										Exception procedure active — access is denied until the exception is accepted.
										{event.exception?.reason && (
											<>
												<br />
												Reason: {event.exception.reason}
											</>
										)}
									</Alert>
								) : null}
								{event.exception?.isException && event.status !== 'exception' ? (
									<Alert severity="warning" variant="filled">
										This event was accepted via the exception procedure.
										{event.exception?.reason && (
											<>
												<br />
												Exception reason: {event.exception.reason}
											</>
										)}
										{event.exception?.acceptReason && (
											<>
												<br />
												Accept reason: {event.exception.acceptReason}
											</>
										)}
									</Alert>
								) : null}
							</GenericDetailPage.Content.Notifications>
							<EventInfoBox
								event={event}
								openEditForm={() => setIsFormOpened(true)}
								openArchiveDialog={() => setIsEventArchiveDialogOpen(true)}
								openDuplicateDialog={() => setIsEventDuplicateDialogOpen(true)}
								permissions={{
									canEdit:
										verifyPermission([PERMISSIONS.personalAllEventsEdit]) ||
										(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwnEvent),
									canArchive:
										verifyPermission([PERMISSIONS.personalAllEventsArchive]) ||
										(verifyPermission([PERMISSIONS.personalOwnEventsArchive]) && isOwnEvent),
									canDuplicate: verifyPermission([PERMISSIONS.personalAllEventsCreate]),
									canReview: false,
									canOpenGroupDetail: verifyPermission([PERMISSIONS.personalGroupsView]),
								}}
								usesSubEvents={usesSubEvents}
							/>
						</GenericDetailPage.Content>
						<Tabs
							variant="underline"
							value={selectedTabIndex >= 0 ? selectedTabIndex : 0}
							onChange={(value) => handleTabChange(value)}
							className="px-4 border-b border-gray-300"
						>
							<Tabs.TabList>
								{tabs.map((tab, index) => (
									<Tabs.Tab
										key={tab.id}
										value={index}
										label={
											<div className="flex items-center gap-2">
												{tab.label}
												{tab.error && <span className="inline-flex h-2 w-2 rounded-full bg-red-600" />}
											</div>
										}
									/>
								))}
							</Tabs.TabList>
						</Tabs>
						<Outlet />
					</GenericDetailPage>
				</div>
			</div>

			<ConfirmDialog
				isOpen={isEventArchiveDialogOpen}
				cancelAction={() => setIsEventArchiveDialogOpen(false)}
				confirmAction={archiveEvent}
				cancelLabel="Cancel"
				confirmLabel="Archive"
			>
				Are you sure you want to archive this event?
			</ConfirmDialog>

			{isEventDuplicateDialogOpen && event && (
				<DuplicateEventDialog
					isOpen={true}
					onClose={() => setIsEventDuplicateDialogOpen(false)}
					onSubmit={duplicateEvent}
					event={event}
					types={types}
					singleGroupAsOwner={usesSubEvents}
					usesSubEvents={usesSubEvents}
				/>
			)}

			<EventForm
				isOpen={isFormOpened}
				data={defaultEventData as Partial<Event>}
				onClose={() => setIsFormOpened(false)}
				onSubmit={handleEditEvent}
				types={types}
				tags={tags}
				singleGroupAsOwner={usesSubEvents}
				usesSubEvents={usesSubEvents}
				usesTags={usesTags}
			/>
		</>
	);
}
